import { useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { InlineNotification } from '@carbon/react';
import {
  notificationsAtom,
  dismissNotificationAtom,
  type AppNotification,
  type NotificationGroup,
} from './atoms';
import styles from './notifications.module.scss';

interface AtomisedNotificationsProps {
  /** Render only notifications from a specific group. Omit to show all. */
  groupIds?: NotificationGroup[];
}

export const AtomisedNotifications = ({
  groupIds,
}: AtomisedNotificationsProps) => {
  const allNotifications = useAtomValue(notificationsAtom);
  const dismiss = useSetAtom(dismissNotificationAtom);
  const autoHideTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const notifications = useMemo<AppNotification[]>(() => {
    const items = groupIds
      ? groupIds.map((groupId) => allNotifications[groupId] ?? []).flat()
      : Object.values(allNotifications).flat();

    return items
      .slice()
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
  }, [allNotifications, groupIds]);

  useEffect(() => {
    for (const n of notifications) {
      if (!n.autoHideMs || autoHideTimers.current.has(n.id)) continue;
      const timer = setTimeout(() => {
        autoHideTimers.current.delete(n.id);
        dismiss(n.id);
      }, n.autoHideMs);
      autoHideTimers.current.set(n.id, timer);
    }
  }, [notifications, dismiss]);

  useEffect(() => {
    const timers = autoHideTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  if (notifications.length === 0) return null;

  return (
    <div className={styles.container}>
      {notifications.map((n) => (
        <InlineNotification
          className={styles.notification}
          lowContrast
          key={n.id}
          title={n.title}
          subtitle={n.message}
          kind={n.kind}
          hideCloseButton={n.hideCloseButton ?? false}
          onCloseButtonClick={() => dismiss(n.id)}
        />
      ))}
    </div>
  );
};
